import { useEffect } from 'react';

/**
 * Adds a `<link rel="stylesheet">` for `href` to the document head the first
 * time a component using it mounts — for a stylesheet (a web font, say) that
 * only one screen needs, so it isn't downloaded or applied anywhere else.
 *
 * Idempotent: every caller asking for the same href shares one tag. The tag is
 * deliberately left in place on unmount — pulling a font sheet out mid-visit
 * would make text that is still on screen reflow back to the fallback face.
 *
 * @param {string|null|undefined} href - absolute stylesheet URL; falsy does nothing
 * @returns {void}
 */
function useStylesheet(href) {
  useEffect(() => {
    if (!href || typeof document === 'undefined') return;

    // Compared by attribute rather than a CSS selector: a Google Fonts URL is
    // full of characters (`:`, `;`, `@`) a selector would need escaping for.
    const alreadyLoaded = Array.from(document.head.querySelectorAll('link[rel="stylesheet"]'))
      .some(link => link.getAttribute('href') === href);
    if (alreadyLoaded) return;

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }, [href]);
}

export default useStylesheet;
